/** @jsxImportSource react */
import { useState, useRef, useEffect, type FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { Link } from '@tanstack/react-router';

interface SearchResult {
	id: number;
	title: string;
	description: string;
	url: string;
}

const fetchSearchResults = async (query: string): Promise<SearchResult[]> => {
	if (!query) return [];
	const response = await fetch(`/api/search?q=${query}`);
	const data = await response.json();
	return data.results; // Access the results array from the response
};

const SiteSearch: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const searchRef = useRef<HTMLDivElement>(null);

	const {
		data: searchResults,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['search', searchQuery],
		queryFn: () => fetchSearchResults(searchQuery),
		enabled: searchQuery.length > 0,
	});

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
		setIsOpen(true);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<div className="relative" ref={searchRef}>
			<div className="relative">
				<Input
					type="text"
					value={searchQuery}
					onChange={handleSearch}
					className="w-36 rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
				/>
				<Search className="absolute right-3 top-2.5 text-gray-400" size={20} />
			</div>
			{isOpen && (searchQuery.length > 0 || isLoading || error) && (
				<div className="absolute z-10 mt-2 max-h-96 w-36 overflow-y-auto rounded-md border border-gray-300 shadow-lg">
					{isLoading && <p className="p-4 text-gray-600">Loading...</p>}
					{error && <p className="p-4 text-red-500">Error: {(error as Error).message}</p>}
					{searchResults && searchResults.length > 0 && (
						<ul className="divide-y divide-gray-200 bg-background">
							{searchResults.map((result) => (
								<li key={result.id} className="p-4 hover:bg-accent hover:text-accent-foreground">
									<Link to={result.url}>
										<h3 className="font-semibold">{result.title}</h3>
										<p className="text-sm">{result.description.slice(0, 100)}...</p>
									</Link>
								</li>
							))}
						</ul>
					)}
					{searchResults && searchResults.length === 0 && searchQuery.length > 0 && (
						<p className="p-4 text-gray-600">No results found</p>
					)}
				</div>
			)}
		</div>
	);
};

export default SiteSearch;
